
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SalaryVerificationSettings as SettingsType } from '@/types/salaryVerification';

interface SalaryVerificationSettingsProps {
  settings: SettingsType | null;
  onUpdateSettings: (settings: Partial<SettingsType>) => Promise<boolean>;
}

export function SalaryVerificationSettings({
  settings,
  onUpdateSettings
}: SalaryVerificationSettingsProps) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize state only when settings are first loaded
  useEffect(() => {
    if (settings) {
      setIsEnabled(settings.is_enabled);
      setMessage(settings.verification_message || '');
    }
  }, [settings?.id]); // Only trigger when settings ID changes, not on every settings update

  const handleSave = async () => {
    setIsUpdating(true);
    
    const success = await onUpdateSettings({
      is_enabled: isEnabled,
      verification_message: message
    });

    setIsUpdating(false);
  };

  const handleToggle = async (enabled: boolean) => {
    setIsEnabled(enabled);
    setIsUpdating(true);
    
    await onUpdateSettings({
      is_enabled: enabled,
      verification_message: message
    });

    setIsUpdating(false);
  };

  if (!settings) {
    return (
      <Card className="p-6">
        <div className="text-center">กำลังโหลดการตั้งค่า...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">การตั้งค่าระบบยืนยันเงินเดือนครู</h3>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-base font-medium">เปิดใช้งานระบบยืนยัน</Label>
            <p className="text-sm text-muted-foreground">
              เมื่อเปิดใช้งาน ครูจะต้องยืนยันข้อมูลก่อนเข้าดูเงินเดือน
            </p>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={handleToggle}
            disabled={isUpdating}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">ข้อความแจ้งเตือน</Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="กรุณาใส่ข้อความที่จะแสดงให้ครูเห็นเมื่อต้องยืนยันข้อมูล"
            rows={3}
          />
        </div>

        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={isUpdating}
          >
            {isUpdating ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
          </Button>
        </div>

        <div className="pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            <p><strong>สถานะปัจจุบัน:</strong> {isEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}</p>
            <p><strong>อัพเดทล่าสุด:</strong> {new Date(settings.updated_at).toLocaleString('th-TH')}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}
